import { useEffect, useMemo, useRef, useState } from 'react';

import { normalizeBulletSpacing } from '@revelio/iso-utility';

import { formatDescriptionRequest } from '../../../api/format-description-api';
import { shouldFormatDescription } from '../../../api/format-description-helpers';
import type { WorkExperienceEntry } from './types';

const MARKDOWN_BULLET_LINE_REGEX = /^\s*[-*+]\s+\S/m;
const INLINE_BULLET_MARKER_REGEX = /\s[-*+•]\s*\S/g;

type FormattingEntry = {
  index: number;
  description: string;
};

type FormattingResult = {
  index: number;
  formattedText: string | null;
};

const hasInlineBulletMarkers = (description: string): boolean => {
  if (MARKDOWN_BULLET_LINE_REGEX.test(description)) {
    return false;
  }
  const matches = description.match(INLINE_BULLET_MARKER_REGEX);
  return (matches?.length ?? 0) >= 2;
};

const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  if (!('name' in error)) {
    return false;
  }
  return (error as { name: string }).name === 'AbortError';
};

const getEntriesToFormat = (
  workExperience: WorkExperienceEntry[]
): FormattingEntry[] =>
  workExperience
    .map((entry, index) => ({
      index,
      description: entry.description,
      isFormatted: Boolean(entry.isFormatted),
    }))
    .filter(({ description, isFormatted }) => {
      if (!shouldFormatDescription(description)) {
        return false;
      }
      if (!isFormatted) {
        return true;
      }
      return hasInlineBulletMarkers(description);
    })
    .map(({ index, description }) => ({ index, description }));

const getFormattingIndices = (entries: FormattingEntry[]): Set<number> =>
  new Set(entries.map(({ index }) => index));

const getEntriesSignature = (entries: FormattingEntry[]): string =>
  JSON.stringify(entries.map(({ index, description }) => [index, description]));

const formatSingleDescription = async (
  signal: AbortSignal,
  entry: FormattingEntry
): Promise<FormattingResult> => {
  try {
    const result = await formatDescriptionRequest({
      text: entry.description,
      signal,
    });
    return { index: entry.index, formattedText: result.formattedText };
  } catch (error) {
    if (!isAbortError(error)) {
      console.error(
        `Failed to format description for entry ${entry.index}:`,
        error
      );
    }
    return { index: entry.index, formattedText: null };
  }
};

export const useFormattedWorkExperience = (
  workExperience: WorkExperienceEntry[]
) => {
  const [formattedDescriptions, setFormattedDescriptions] = useState<
    Map<number, string>
  >(new Map());
  const [formattingIndices, setFormattingIndices] = useState<Set<number>>(() =>
    getFormattingIndices(getEntriesToFormat(workExperience))
  );
  const entriesToFormat = useMemo(
    () => getEntriesToFormat(workExperience),
    [workExperience]
  );
  const entriesSignature = useMemo(
    () => getEntriesSignature(entriesToFormat),
    [entriesToFormat]
  );
  const entriesToFormatRef = useRef<FormattingEntry[]>([]);
  entriesToFormatRef.current = entriesToFormat;

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    let isCancelled = false;
    const controller = new AbortController();
    const entriesToFormatSnapshot = entriesToFormatRef.current;

    const resetFormattingState = () => {
      if (isCancelled) {
        return;
      }
      setFormattedDescriptions(new Map());
      setFormattingIndices(getFormattingIndices(entriesToFormatSnapshot));
    };

    const applyFormattedResults = (
      results: FormattingResult[],
      entries: FormattingEntry[]
    ) => {
      if (isCancelled) {
        return;
      }
      setFormattedDescriptions((prev) => {
        const updated = new Map(prev);
        results.forEach(({ index, formattedText }) => {
          if (formattedText) {
            updated.set(index, formattedText);
          }
        });
        return updated;
      });
      setFormattingIndices((prev) => {
        const updated = new Set(prev);
        // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
        entries.forEach(({ index }) => updated.delete(index));
        return updated;
      });
    };

    const formatDescriptions = async () => {
      resetFormattingState();
      if (entriesToFormatSnapshot.length === 0) {
        return;
      }
      await Promise.all(
        entriesToFormatSnapshot.map(async (entry) => {
          const result = await formatSingleDescription(
            controller.signal,
            entry
          );
          if (isCancelled) {
            return;
          }
          applyFormattedResults([result], [entry]);
        })
      );
    };

    formatDescriptions();
    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [entriesSignature]);

  const formattedWorkExperience = useMemo(
    () =>
      workExperience.map((entry, index) => {
        const formattedDescription = formattedDescriptions.get(index);
        const description =
          formattedDescription ?? normalizeBulletSpacing(entry.description);
        return {
          ...entry,
          description,
          isFormatted:
            formattedDescriptions.has(index) || Boolean(entry.isFormatted),
        };
      }),
    [formattedDescriptions, workExperience]
  );

  return {
    workExperience: formattedWorkExperience,
    formattingIndices,
  };
};
